import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Notice } from '@verlo/shared/components/ui/notice';
import { settingsApi } from '@verlo/shared/services/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useInsight } from '../../../components/office';

/**
 * Settings › Business › Verlo Insight. On/off for the business, the daily cap, and how
 * much of today's cap is used. Whether the platform has a key at all is not this page's
 * to change — it says so when it hasn't.
 */
const InsightCard = () => {
  const { status, refresh } = useInsight();
  const [form, setForm] = useState({ enabled: true, daily_cap: 200 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi.get().then(({ data }) => {
      const ins = data?.insight || {};
      setForm({ enabled: ins.enabled ?? true, daily_cap: ins.daily_cap ?? 200 });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.update({ insight: { enabled: !!form.enabled, daily_cap: Math.max(0, parseInt(form.daily_cap, 10) || 0) } });
      await refresh();
      toast.success('Verlo Insight settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-insight">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Verlo Insight</CardTitle>
        <CardDescription>The plain-English reads in Reports: what moved the P&amp;L, why a number moved, what needs attention today.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status && !status.configured && (
          <Notice tone="info" word="Platform" title="Not set up on this deployment yet">
            Verlo Insight switches on when the platform has its key. Nothing shows in Reports until then.
          </Notice>
        )}
        <label className="flex items-center justify-between gap-3 rounded-md border border-hairline px-3 py-2 text-[13.5px] text-ink">
          On for this business
          <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="settings-insight-enabled" />
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="insight-cap" className="text-[12.5px] text-ink-soft">Reads per day</Label>
            <Input id="insight-cap" type="number" min="0" inputMode="numeric" className="font-mono tabular-nums" value={form.daily_cap} onChange={(e) => setForm({ ...form, daily_cap: e.target.value })} data-testid="settings-insight-cap" />
            <p className="text-[12px] text-ink-faint">A read costs about a penny. 200 a day is plenty for one site.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12.5px] text-ink-soft">Today</Label>
            <div className="font-mono text-[24px] leading-none text-ink tabular-nums">{status?.today?.calls ?? 0} <span className="text-[14px] text-ink-faint">/ {status?.today?.cap ?? form.daily_cap}</span></div>
            <p className="text-[12px] text-ink-faint">{status?.model ? `Reads by ${status.model}. ` : ''}Every read is on the Audit page with its cost.</p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="settings-insight-save">
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InsightCard;
